import find from 'lodash/find';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { isEditable } from 'helpers/menus';
import * as fromSendABox from 'reducers/sendABox';

import basketEdits, * as fromBasketEdits from './basketEdits';
import baskets, * as fromBaskets from './baskets';
import basketStatus, * as fromBasketStatus from './basketStatus';
import basketSummaryStatus from './basketSummaryStatus';
import mealOptionGroups, * as fromMealOptionGroups from './mealOptionGroups';
import mealPrices, * as fromMealPrices from './mealPrices';
import mealReviewStats from './mealReviewStats';
import mealSellouts from './mealSellouts';
import menuCategories, * as fromMenuCategories from './menuCategories';
import menuMeals, * as fromMenuMeals from './menuMeals';
import menus, * as fromMenus from './menus';
import menuSlugs from './menuSlugs';
import menuStatus, * as fromMenuStatus from './menuStatus';
import modalMeals, * as fromModalMeals from './modalMeals';
import quickFilters, * as fromQuickFilters from './quickFilters';
import reviews, * as fromReviews from './reviews';
import status, * as fromStatus from './status';

export default combineReducers({
  basketEdits,
  baskets,
  basketStatus,
  basketSummaryStatus,
  mealOptionGroups,
  mealPrices,
  mealReviewStats,
  menuCategories,
  menuMeals,
  menus,
  menuSlugs,
  menuStatus,
  modalMeals,
  quickFilters,
  reviews,
  status,
  mealSellouts,
});

const selectV3Dashboard = state => state.v3.dashboard;
const selectMenuStatus = state => selectV3Dashboard(state).menuStatus;
const selectMenus = state => selectV3Dashboard(state).menus;
const selectMenuMealsSlice = state => selectV3Dashboard(state).menuMeals;
const selectMealOptionGroups = state => selectV3Dashboard(state).mealOptionGroups;
const selectMenuCategories = state => selectV3Dashboard(state).menuCategories;
const selectBaskets = state => selectV3Dashboard(state).baskets;
const selectBasketStatus = state => selectV3Dashboard(state).basketStatus;
const selectDashboardStatus = state => selectV3Dashboard(state).status;
const selectBasketEdits = state => selectV3Dashboard(state).basketEdits;
const selectModalMeals = state => selectV3Dashboard(state).modalMeals;
const selectQuickFiltersState = state => selectV3Dashboard(state).quickFilters;
const selectReviewsState = state => selectV3Dashboard(state).reviews;
const selectMealPricesState = state => selectV3Dashboard(state).mealPrices;
const selectMealReviewStatsState = state => selectV3Dashboard(state).mealReviewStats;

export const selectMealSellouts = state => selectV3Dashboard(state).mealSellouts;
export const selectMenuSlugs = state => selectV3Dashboard(state).menuSlugs;

export const selectMenu = (state, menuSlug) => fromMenus.selectMenu(selectMenus(state), menuSlug);
export const menuNeedsInitialization = (state, menuSlug) =>
  fromMenuStatus.needsInitialization(selectMenuStatus(state), menuSlug);
export const isMenuLoading = (state, menuSlug) =>
  fromMenuStatus.isMenuLoading(selectMenuStatus(state), menuSlug);
export const isMenuError = (state, menuSlug) =>
  fromMenuStatus.isMenuError(selectMenuStatus(state), menuSlug);
export const isMenuRefreshLoading = (state, menuSlug) =>
  fromMenuStatus.isMenuRefreshLoading(selectMenuStatus(state), menuSlug);

export const selectAllMealsForMenu = (state, menuSlug) =>
  fromMenuMeals.selectAllMenuMeals(selectMenuMealsSlice(state), menuSlug, null);
export const selectMealsForMenu = (state, menuSlug, stageStep) =>
  fromMenuMeals.selectMenuMeals(selectMenuMealsSlice(state), menuSlug, stageStep);
export const selectMealAddons = (state, menuSlug, stageStep) =>
  fromMenuMeals.selectMealAddons(selectMenuMealsSlice(state), menuSlug, stageStep);
export const selectMealOptionGroupsForMenu = (state, menuSlug) =>
  fromMealOptionGroups.selectMealOptionGroupsForMenu(selectMealOptionGroups(state), menuSlug);
export const selectMealOptionGroupsForMenuAndMeal = (state, menuSlug, mealId) =>
  fromMealOptionGroups.selectMealOptionGroupsForMenuAndMeal(
    selectMealOptionGroups(state),
    menuSlug,
    mealId
  );
export const selectMealOptionGroup = (state, menuSlug, mealId, mealOptionGroupId) =>
  fromMealOptionGroups.selectMealOptionGroup(
    selectMealOptionGroups(state),
    menuSlug,
    mealId,
    mealOptionGroupId
  );
export const selectMealForMenu = (state, menuSlug, mealId, stageStep = null) =>
  find(selectMealsForMenu(state, menuSlug, stageStep), { id: mealId });
export const selectOptIns = (state, menuSlug, stageStep) =>
  fromMenuMeals.selectOptIns(selectMenuMealsSlice(state), menuSlug, stageStep);
export const selectNonDonationMealsForMenu = (state, menuSlug, stageStep) =>
  fromMenuMeals.selectNonDonationMealsForMenu(selectMenuMealsSlice(state), menuSlug, stageStep);

export const selectMenuCategoriesLoading = (state, menuSlug) =>
  fromMenuCategories.selectMenuCategoriesLoading(selectMenuCategories(state), menuSlug);
export const selectMenuCategoriesData = (state, menuSlug) =>
  fromMenuCategories.selectMenuCategoriesData(selectMenuCategories(state), menuSlug);

export const selectModalMealsForMenu = (state, menuSlug) =>
  fromModalMeals.selectModalMealsForMenu(selectModalMeals(state), menuSlug);

export const selectBasket = (state, menuSlug) =>
  fromBaskets.selectBasket(selectBaskets(state), menuSlug);
export const selectMealsForBasket = (state, menuSlug) =>
  fromBaskets.selectMealsForBasket(selectBaskets(state), menuSlug);

export const isBasketLoading = (state, menuSlug) =>
  fromBasketStatus.isBasketLoading(selectBasketStatus(state), menuSlug);
export const isBasketError = (state, menuSlug) =>
  fromBasketStatus.isBasketError(selectBasketStatus(state), menuSlug);
export const basketNeedsInitialization = (state, menuSlug) =>
  fromBasketStatus.needsInitialization(selectBasketStatus(state), menuSlug);
export const removedMeals = (state, menuSlug) =>
  fromBasketStatus.removedMeals(selectBasketStatus(state), menuSlug);

export const isInitializationComplete = state =>
  fromStatus.isInitializationComplete(selectDashboardStatus(state));
export const needsInitializing = state =>
  fromStatus.needsInitializing(selectDashboardStatus(state));
export const isUnauthorized = state => fromStatus.isUnauthorized(selectDashboardStatus(state));

export const selectMealServingsForBasketEdits = state =>
  fromBasketEdits.selectServings(selectBasketEdits(state));
export const selectMealOptionsForBasketEdits = state =>
  fromBasketEdits.selectMealOptions(selectBasketEdits(state));
export const selectMealOptionsForMeal = (state, mealId) =>
  fromBasketEdits.selectMealOptionsForMeal(selectBasketEdits(state), mealId);
export const selectSubtotalsForBasketEdits = state =>
  fromBasketEdits.selectSubtotals(selectBasketEdits(state));
export const selectNutritionSummariesForBasketEdits = state =>
  fromBasketEdits.selectNutritionSummaries(selectBasketEdits(state));
export const selectPairingsForBasketEdits = state =>
  fromBasketEdits.selectSuggestedPairings(selectBasketEdits(state));
export const selectBasketDirty = state =>
  fromBasketEdits.selectBasketDirty(selectBasketEdits(state));
export const selectBasketPending = state =>
  fromBasketEdits.selectBasketPending(selectBasketEdits(state));
export const selectBasketEditsIsLoaded = state =>
  fromBasketEdits.selectBasketIsLoaded(selectBasketEdits(state));

const sortAsc = (x, y) => x - y;

export const selectOrderedBaskets = createSelector(selectBaskets, baskets => {
  return Object.values(baskets).sort((basketA, basketB) => {
    return sortAsc(new Date(basketA.menuEndsAt), new Date(basketB.menuEndsAt));
  });
});

export const selectNextOrderableBasket = createSelector(selectOrderedBaskets, orderedBaskets => {
  return find(orderedBaskets, {
    scheduled: true,
    futureBasket: true,
  });
});

export const selectQuickFilters = (state, stageStep) =>
  fromQuickFilters.selectQuickFilters(selectQuickFiltersState(state), stageStep);

export const selectCompletedReviews = state =>
  fromReviews.selectCompletedReviews(selectReviewsState(state)).reviews;
export const selectCompletedReviewsLoading = state =>
  fromReviews.selectCompletedReviews(selectReviewsState(state)).isLoading;
export const selectCompletedReviewsFetched = state =>
  fromReviews.selectCompletedReviews(selectReviewsState(state)).isFetched;

export const selectMealReviewStats = (state, mealId, menuSlug) =>
  selectMealReviewStatsState(state)[menuSlug]?.[mealId] || {};

export const makeV3SelectRelativeMenuWeek = () =>
  createSelector(
    [selectMenuSlugs, selectBaskets, (_state, menuSlug) => menuSlug],
    (slugs, basketsBySlug, menuSlug) => {
      const editableSlugs = (slugs || []).filter(slug => isEditable(basketsBySlug?.[slug]));
      const idx = editableSlugs.indexOf(menuSlug);
      return idx >= 0 ? idx : null;
    }
  );
export const selectMealPriceForMealDetails = (state, menuSlug, mealId, isSendABox) => {
  if (mealId == null || menuSlug === null) return null;
  if (isSendABox) {
    return fromSendABox.sendABoxMealPrice(state, mealId);
  } else {
    return fromMealPrices.selectMealPrice(selectMealPricesState(state), menuSlug, mealId);
  }
};
